import { useEffect, useState } from "react";
import PopUp from "./PopUp";
import type { Takeover } from "./takeover";

interface TakeoverNoticeProps {
  takeover: Takeover;
  // Called on any deliberate action — opened, followed, or waved away. Being
  // shown the notice is not one of them.
  onDismiss: () => void;
}

// News used to open as a modal the instant the game ended, which landed on top
// of the solved screen and the share button.
//
// Instead this is a line of the page underneath those buttons — deliberately not
// a banner, since a banner is the thing people have learned to look past. It
// reads as the game's own text, earns its glance through motion (it rises in and
// a highlight sweeps across it once, then clears), and only opens the full
// announcement if the player taps it.
export function TakeoverNotice({ takeover, onDismiss }: TakeoverNoticeProps) {
  const [shown, setShown] = useState(false);
  const [open, setOpen] = useState(false);
  const [hidden, setHidden] = useState(false);

  // Let the solved screen have the moment to itself before sliding in.
  useEffect(() => {
    const timer = window.setTimeout(() => setShown(true), 900);
    return () => window.clearTimeout(timer);
  }, []);

  const title = (takeover.title ?? "").trim() || "News!";

  return (
    <>
      {!hidden && (
      <div className={"takeover-notice" + (shown ? " takeover-notice--in" : "")}>
        <button
          type="button"
          className="takeover-notice-open"
          onClick={() => {
            // Opening it is a deliberate action, so it is recorded straight
            // away — the line itself stays put underneath until the
            // announcement is closed.
            setOpen(true);
            onDismiss();
          }}
        >
          <span className="takeover-notice-dot" aria-hidden="true" />
          <span className="takeover-notice-swipe">
            <span className="takeover-notice-lead">New:</span>{" "}
            <span className="takeover-notice-title">{title}</span>{" "}
            <span className="takeover-notice-more" aria-hidden="true">
              {"\u203A"}
            </span>
          </span>
        </button>
        <button
          type="button"
          className="takeover-notice-close"
          aria-label="Dismiss news"
          onClick={() => {
            onDismiss();
            setHidden(true);
          }}
        >
          {"\u00D7"}
        </button>
      </div>
      )}
      {open && (
        <PopUp
          title={title}
          element={
            <div className="takeover-announcement">
              {takeover.imageUrl && (
                <img className="takeover-image" src={takeover.imageUrl} alt="" />
              )}
              {takeover.message && (
                <p className="takeover-message">{takeover.message}</p>
              )}
              {takeover.ctaUrl && (
                <div className="takeover-cta">
                  <a
                    href={takeover.ctaUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="takeover-button"
                    onClick={onDismiss}
                  >
                    {(takeover.ctaText ?? "").trim() || "Learn more"}
                  </a>
                </div>
              )}
            </div>
          }
          onClick={() => {
            setOpen(false);
            setHidden(true);
          }}
        />
      )}
    </>
  );
}

export default TakeoverNotice;
